import type { Roles, UserAccountFragment } from "@qw/graphql";
import { isInternal, isInternalAgentDomain } from "@qw/qw-common";
import { intersection } from "lodash";
import type { PropsWithChildren } from "react";
import { useAccount } from "../hooks/use-account";

interface Props {
  roles: Roles[];
  internal?: boolean;
  elseShow?: React.ReactNode;
}

export const HasRole: React.FC<PropsWithChildren<Props>> = ({ roles, children, elseShow = null }) => {
  const { user } = useAccount();

  // does the user have one of the specified roles?
  const validateRoles = intersection(user.roles, roles).length > 0;

  return validateRoles ? <>{children}</> : <>{elseShow}</>;
};

// B-1441: these gates now use the shared `isInternal` (flag || role), which intentionally widens them
// from the old flag-only check — that widening is the fix for the B-1370 class (a role-internal user
// with internal=false was being locked out of internal UI). It is a no-op on live data: the flag and
// role terms coincide on every active user (verified on the read replica). Some brokerage gates still
// read raw `user.internal` (e.g. redirect-routes, app-nav, use-chat-scope); migrating those to
// `isInternal` is a separate, reversible step tracked under B-1441, not this PR.
export const HasInternalRole: React.FC<PropsWithChildren<Pick<Props, "elseShow">>> = ({ children, elseShow }) => {
  const { user } = useAccount();
  return isInternal(user) ? <>{children}</> : <>{elseShow}</>;
};

export const HasExternalRole: React.FC<PropsWithChildren<Pick<Props, "elseShow">>> = ({ children, elseShow }) => {
  const { user } = useAccount();
  return isInternal(user) ? <>{elseShow}</> : <>{children}</>;
};

export const hasRole = (user: { roles: Roles[]; internal: boolean } | undefined, hasRoles: Roles[]) => {
  return user && intersection(user.roles, hasRoles).length > 0;
};

export const hasInternalRoleOrInternalAdmin = (user: Pick<UserAccountFragment, "roles" | "internal" | "email">) => {
  return isInternal(user) || isInternalAgentDomain(user);
};
